import type { SiteDetail, SitePrice, SiteStatus } from '@ev/shared'
import { useCallback } from 'react'
import { loadPrices, loadSite, loadStatus } from '../data/cache'
import { useDataHealthStore } from '../data/health'
import { useResource } from '../data/resource'
import { useStrings } from '../i18n'
import { selectSelectedSiteId, useAppStore } from '../state/store'
import { ChargeCardTiles } from './ChargeCardTiles'
import { UnconfirmedNotice } from './confirmation/UnconfirmedNotice'
import { DetailError, DetailSkeleton } from './DetailStates'
import { SiteSummary } from './SiteSummary'
import { StationBreakdown } from './StationBreakdown'

/**
 * The site detail panel.
 *
 * `sites/{id}.json` is fetched the moment a site is selected and not a second
 * before - the country's worth of detail payloads is far too much to ship on
 * first paint, which is exactly why the pipeline splits them out.
 *
 * Live status and tariffs are layered on top but are never allowed to sink the
 * panel: if `status.json` or `prices.json` fails, the address, the connectors
 * and the voltages still render, and the two supplementary fields say they have
 * no data. Only the site payload itself is fatal.
 *
 * On a wide screen this is the right-hand rail. Below 60rem the same markup
 * becomes a bottom sheet, in CSS - one component, two shapes, no duplicated
 * render tree to keep in step.
 */

interface DetailPayload {
  readonly site: SiteDetail
  readonly status: SiteStatus | undefined
  readonly price: SitePrice | undefined
}

/**
 * The two supplementary reads report their own failure into the data-health
 * store rather than throwing: a site whose live status did not load still has an
 * address, connectors and voltages worth showing, and the banner in the chrome
 * is where "availability is unavailable" belongs - said once for the whole page,
 * not repeated in every panel that noticed.
 */
const loadDetail = async (siteId: string): Promise<DetailPayload> => {
  const { markUnavailable, markAvailable } = useDataHealthStore.getState()

  const [site, status, price] = await Promise.all([
    loadSite(siteId),
    loadStatus()
      .then((artifact) => {
        markAvailable('status')
        return artifact.sites[siteId]
      })
      .catch(() => {
        markUnavailable('status')
        return undefined
      }),
    loadPrices()
      .then((artifact) => {
        markAvailable('prices')
        return artifact.sites[siteId]
      })
      .catch(() => {
        markUnavailable('prices')
        return undefined
      }),
  ])

  return { site, status, price }
}

export const SiteDetailPanel = () => {
  const strings = useStrings()
  const selectedSiteId = useAppStore(selectSelectedSiteId)
  const setSelectedSiteId = useAppStore((state) => state.setSelectedSiteId)
  const { resource, reload } = useResource(selectedSiteId, loadDetail)

  const close = useCallback(() => setSelectedSiteId(null), [setSelectedSiteId])

  if (resource.status === 'idle') return null

  const title =
    resource.status === 'ready'
      ? resource.value.site.name
      : (selectedSiteId ?? strings.detail.title)

  return (
    <article className="ev-detail" aria-busy={resource.status === 'loading'}>
      <header className="ev-detail__header">
        <span className="ev-detail__grabber" aria-hidden="true" />
        <h3 className="ev-detail__title">{title}</h3>
        <button
          type="button"
          className="ev-detail__close"
          onClick={close}
          aria-label={strings.detail.closePanel}
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="ev-detail__body">
        {resource.status === 'loading' && <DetailSkeleton />}

        {resource.status === 'error' && <DetailError failure={resource.failure} onRetry={reload} />}

        {resource.status === 'ready' && (
          <>
            {/* Above the summary, because it qualifies everything under it: a
                reader should know we could not confirm the station before they
                read its connectors as fact. */}
            {resource.value.site.unconfirmed !== undefined && (
              <UnconfirmedNotice
                siteId={resource.value.site.id}
                unconfirmed={resource.value.site.unconfirmed}
              />
            )}
            {/* Above the summary because it is what the driver came to do:
                the counts and the tariff are read after the charger is chosen,
                not before. */}
            <ChargeCardTiles site={resource.value.site} />
            <SiteSummary
              site={resource.value.site}
              status={resource.value.status}
              price={resource.value.price}
            />
            <h3 className="ev-detail__section">{strings.detail.stations}</h3>
            <StationBreakdown site={resource.value.site} status={resource.value.status} />
          </>
        )}
      </div>
    </article>
  )
}
